import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ANSWER_MODES, CONFIDENCE_LEGEND } from '../../../shared/catalog.js'
import { formatDate, formatRelative } from '../../lib/dates.js'
import { useDeleteSkillFile, useDeleteSystem, useDiscardDraft, useSystemDetail, useSystemVersions } from '../../lib/queries/systems.js'
import { Icon } from '../icons/Icon.jsx'
import { ConfidenceBadge } from '../ui/Badges.jsx'
import { Dialog } from '../ui/Dialog.jsx'
import { Menu } from '../ui/Menu.jsx'
import { SystemTile } from '../ui/SystemTile.jsx'
import { PublishDialog } from './PublishDialog.jsx'
import { SkillFileDrawer } from './SkillFileDrawer.jsx'
import { SystemFormDialog } from './SystemFormDialog.jsx'
import { UploadDialog } from './UploadDialog.jsx'
import l from './library.module.css'

const STAT_LABELS = [
  ['pipes', 'standard pipes'],
  ['operations', 'operations'],
  ['gotchas', 'top gotchas'],
  ['modes', 'answer modes'],
]

function legendText(system, level) {
  const short = system.shortName ?? system.name
  if (level === 'proven') return `Used in a DCKAP ${short} implementation`
  if (level === 'documented') return `${system.vendorShort ?? system.vendor ?? 'Vendor'} or verified community docs, not yet used by DCKAP`
  return CONFIDENCE_LEGEND.confirm
}

/**
 * The detail panel (§6.4 right). View mode shows the published skill; edit
 * mode (?edit=1) works on the draft, which is created on the first save.
 */
export function SystemDetail({ system, search, canEdit, canDelete }) {
  const navigate = useNavigate()
  const editMode = Boolean(search.edit) && canEdit
  // Which version's files to list and open.
  const version = editMode ? (system.draft ? 'draft' : system.published ? 'published' : 'draft') : system.published ? 'published' : 'draft'
  const detail = useSystemDetail(system.optimistic ? null : system.slug, version)
  const versions = useSystemVersions(system.optimistic ? null : system.slug)
  const discard = useDiscardDraft(system.slug)
  const removeFile = useDeleteSkillFile(system.slug)
  const removeSystem = useDeleteSystem(system.slug)
  const [deleting, setDeleting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)

  const setSearch = (patch) =>
    navigate({ to: '/library/$slug', params: { slug: system.slug }, search: (prev) => ({ ...prev, ...patch }), replace: true })

  const files = detail.data?.files ?? []
  const openFile = search.file
  const drawerMode = search.new ? 'new' : openFile ? (editMode ? 'edit' : 'view') : null
  const stats = system.stats ?? {}
  const label = system.published ? `Skill v${system.published.label}` : system.draft ? 'Draft skill' : 'No skill yet'
  const contract = system.published?.contract ?? system.draft?.contract
  const updated = system.draft?.updatedAt ?? system.published?.publishedAt ?? system.updatedAt

  return (
    <section aria-label={`${system.name} skill`} className={l.detail}>
      <div className={l.detailHead}>
        <SystemTile system={system} size="header" />
        <div className={l.detailTitles}>
          <div className={l.detailNameRow}>
            <h2 className={l.detailName}>{system.name}</h2>
            <span className={l.detailVendor}>{[system.vendor, system.categoryLabel].filter(Boolean).join(' · ')}</span>
          </div>
          <div className={l.tags}>
            {system.skillName ? <span className={`${l.tag} ${l.tagMono}`}>{system.skillName}</span> : null}
            <span className={l.tag}>{label}</span>
            {contract ? <span className={l.tag}>Contract {contract}</span> : null}
            {updated ? <span className={`${l.tag} ${l.tagPlain}`}>Updated {formatDate(updated)}</span> : null}
          </div>
        </div>
        <div className={l.actions}>
          {canEdit ? (
            <button type="button" className={`btn btn-ghost ${l.actionButton}`} onClick={() => setUploading(true)}>
              <Icon name="arrow-up" size={15} strokeWidth={1.6} />
              Upload skill
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className={`btn btn-ghost ${l.actionButton}`}
              aria-pressed={editMode}
              onClick={() => setSearch({ edit: editMode ? undefined : true, file: undefined, new: undefined })}
            >
              <Icon name={editMode ? 'check' : 'edit'} size={15} strokeWidth={1.6} />
              {editMode ? 'Done editing' : 'Edit skill'}
            </button>
          ) : null}
          {system.status !== 'none' ? (
            <Link to="/new" className={`btn btn-teal ${l.actionButton}`}>
              Ask a question
            </Link>
          ) : null}
          {canEdit || canDelete ? (
            <Menu
              trigger={(props) => (
                <button type="button" className={`btn btn-ghost ${l.iconAction}`} aria-label="More system options" {...props}>
                  <Icon name="dots" size={16} />
                </button>
              )}
              items={[
                ...(canEdit ? [{ label: 'Edit details and suggestions', onSelect: () => setEditingDetails(true) }] : []),
                ...(canDelete ? [{ label: 'Delete system', danger: true, disabled: system.optimistic, onSelect: () => setDeleting(true) }] : []),
              ]}
            />
          ) : null}
        </div>
      </div>

      {canEdit && system.draft ? (
        <div className={l.draftBanner} role="status">
          <Icon name="edit" size={15} strokeWidth={1.6} />
          <span>
            Draft with {system.draft.files} {system.draft.files === 1 ? 'file' : 'files'} · edited {formatRelative(system.draft.updatedAt)}.{' '}
            {system.published ? `Answers still use v${system.published.label}.` : 'Answers use this draft until you publish.'}
          </span>
          {!editMode ? (
            <button type="button" className="btn btn-ghost" onClick={() => setSearch({ edit: true })}>
              Open draft
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={discard.isPending}
            onClick={() => {
              if (window.confirm(`Discard the ${system.name} draft? Published files are not affected.`)) discard.mutate()
            }}
          >
            Discard
          </button>
          <button type="button" className="btn btn-teal" disabled={!system.draft.files} onClick={() => setPublishing(true)}>
            Publish…
          </button>
        </div>
      ) : null}

      {system.description ? <p className={l.description}>{system.description}</p> : null}

      {system.status !== 'none' ? (
        <div className={l.stats}>
          {STAT_LABELS.map(([key, text]) => (
            <div key={key} className={l.stat}>
              <span className={l.statNumber}>{stats[key] ?? '—'}</span>
              <span className={l.statLabel}>{text}</span>
            </div>
          ))}
        </div>
      ) : null}

      {system.status !== 'none' ? (
        <div className={l.section}>
          <span className="section-label">Answer modes</span>
          <div className={l.modes}>
            {ANSWER_MODES.map((mode) => (
              <span key={mode.key} className={`${l.mode} ${mode.key === 'lookup' ? l.modeOn : ''}`}>
                {mode.number} · {mode.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className={l.section}>
        <div className={l.sectionHead}>
          <span className="section-label">{editMode ? 'Skill files · draft' : 'Skill files'}</span>
          <span className={l.sectionNote}>
            {editMode ? (
              <button type="button" className="btn btn-ghost" style={{ height: 30 }} onClick={() => setSearch({ new: true, file: undefined })}>
                <Icon name="plus" size={14} strokeWidth={1.7} />
                Add file
              </button>
            ) : (
              'SKILL.md is primary and links the rest'
            )}
          </span>
        </div>
        {detail.isPending && !system.optimistic ? (
          <div className="skeleton" style={{ height: 180, borderRadius: 12 }} />
        ) : files.length === 0 ? (
          <div className={l.emptyFiles}>
            {system.status === 'none'
              ? `${system.name} has no skill yet. Build it from the skill builder prompt, then upload SKILL.md with its references and assets.`
              : 'This version has no files yet. Upload the skill folder to fill it.'}
            {canEdit ? (
              <button type="button" className="btn btn-dark" onClick={() => setUploading(true)}>
                Upload skill files
              </button>
            ) : null}
          </div>
        ) : (
          <ul className={l.files}>
            {files.map((file) => {
              const base = file.path.split('/').pop()
              const primary = file.role === 'primary'
              return (
                <li key={file.path} className={`${l.file} ${primary ? l.filePrimary : ''}`}>
                  <Icon
                    name={file.path.endsWith('.json') ? 'braces' : 'file'}
                    size={16}
                    strokeWidth={1.5}
                    stroke={primary ? '#0E6E6A' : '#5E626A'}
                  />
                  <span className={l.fileText}>
                    <span className={l.fileNameRow}>
                      <button type="button" className={l.fileOpen} onClick={() => setSearch({ file: file.path, new: undefined })}>
                        {file.path}
                      </button>
                      {primary ? <span className={l.primaryBadge}>Primary</span> : null}
                      {file.pending ? <span className={l.pendingBadge}>saving…</span> : null}
                    </span>
                    {file.summary ? <span className={l.fileSummary}>{file.summary}</span> : null}
                  </span>
                  {file.sectionLabel ? <span className={l.fileSection}>{file.sectionLabel}</span> : null}
                  {canEdit ? (
                    <button
                      type="button"
                      className={`icon-btn ${l.fileAction}`}
                      aria-label={`Edit ${base}`}
                      onClick={() => setSearch({ edit: true, file: file.path, new: undefined })}
                    >
                      <Icon name="edit" size={15} strokeWidth={1.6} />
                    </button>
                  ) : null}
                  {editMode && !primary ? (
                    <button
                      type="button"
                      className={`icon-btn ${l.fileAction}`}
                      aria-label={`Remove ${base} from the draft`}
                      onClick={() => {
                        if (window.confirm(`Remove ${file.path} from the draft?`)) removeFile.mutate({ path: file.path })
                      }}
                    >
                      <Icon name="trash" size={15} strokeWidth={1.6} />
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {system.status !== 'none' ? (
        <div className={l.section}>
          <span className="section-label">How answers tag confidence</span>
          <div className={l.legend}>
            {['proven', 'documented', 'confirm'].map((level) => (
              <div key={level} className={l.legendItem}>
                <ConfidenceBadge level={level} />
                {legendText(system, level)}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {versions.data?.length ? (
        <div className={l.section}>
          <span className="section-label">Versions</span>
          <ul className={l.versions}>
            {versions.data.map((v) => (
              <li key={v.id}>
                <span className={l.versionLabel}>{v.label ? `v${v.label}` : 'draft'}</span>
                <span>
                  {v.status}
                  {v.publishedAt ? ` · ${formatDate(v.publishedAt)}` : ''}
                  {v.publishedBy ? ` · ${v.publishedBy}` : ''} · {v.files} files
                  {v.notes ? ` — ${v.notes}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {drawerMode ? (
        <SkillFileDrawer
          system={system}
          version={drawerMode === 'view' ? version : system.draft ? 'draft' : version}
          path={openFile}
          mode={drawerMode}
          canEdit={canEdit}
          onClose={() => setSearch({ file: undefined, new: undefined })}
          onEdit={() => setSearch({ edit: true })}
          onSaved={(path) => setSearch({ edit: true, new: undefined, file: path })}
        />
      ) : null}
      <UploadDialog system={system} open={uploading} onClose={() => setUploading(false)} />
      <Dialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete ${system.name}?`}
        description={`${system.name} and its skill files leave the System library, and new questions no longer search them. Past conversations keep their answers.`}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setDeleting(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={removeSystem.isPending}
              onClick={() => {
                setDeleting(false)
                removeSystem.mutate()
                navigate({ to: '/library' })
              }}
            >
              Delete system
            </button>
          </>
        }
      />
      <SystemFormDialog open={editingDetails} system={system} onClose={() => setEditingDetails(false)} />
      {system.draft ? <PublishDialog system={system} open={publishing} onClose={() => setPublishing(false)} /> : null}
    </section>
  )
}
